<template>
  <div
    v-if="hasEditorLayout && flexLayout"
    class="lisaui:flex lisaui:h-full lisaui:min-h-0 lisaui:min-w-0 lisaui:flex-col lisaui:gap-2 lisaui:overflow-hidden"
  >
    <div
      v-if="showCommonConfig && $slots['common-config'] != null"
      class="lisaui:relative lisaui:z-10 lisaui:min-w-0 lisaui:shrink-0 lisaui:overflow-hidden"
    >
      <slot name="common-config" />
    </div>
    <div
      class="lisaui:relative lisaui:z-0 lisaui:flex lisaui:min-h-0 lisaui:min-w-0 lisaui:flex-1 lisaui:flex-row lisaui:gap-2 lisaui:overflow-hidden"
    >
      <aside
        v-if="$slots['side-navigation'] != null"
        class="lisaui:min-h-0 lisaui:w-64 lisaui:shrink-0 lisaui:overflow-hidden"
        @wheel.stop
      >
        <slot name="side-navigation" />
      </aside>
      <main
        class="lisaui:relative lisaui:z-0 lisaui:min-h-0 lisaui:min-w-0 lisaui:flex-1 lisaui:overflow-hidden"
        @wheel.stop
      >
        <slot />
      </main>
      <aside
        v-if="$slots.settings != null"
        class="lisaui:min-h-0 lisaui:min-w-48 lisaui:max-w-80 lisaui:flex-1 lisaui:overflow-hidden"
        @wheel.stop
      >
        <slot name="settings" />
      </aside>
    </div>
  </div>
  <div
    v-else-if="hasEditorLayout"
    class="input-frame-layout"
    :class="{
      'input-frame-layout--independent-scroll': independentScroll,
      'input-frame-layout--without-side-navigation': !hasSideNavigation,
      'input-frame-layout--wide-settings': settingsWide,
    }"
  >
    <div
      v-if="showCommonConfig && $slots['common-config'] != null"
      class="input-frame-layout__common-config"
    >
      <slot name="common-config" />
    </div>
    <aside v-if="$slots['side-navigation'] != null" class="input-frame-layout__side-navigation">
      <slot name="side-navigation" />
    </aside>
    <main class="input-frame-layout__content">
      <slot />
    </main>
    <aside v-if="$slots.settings != null" class="input-frame-layout__settings">
      <slot name="settings" />
    </aside>
  </div>
  <slot v-else />
</template>

<script setup lang="ts">
import { computed, useSlots } from "vue";

const {
  showCommonConfig = true,
  independentScroll = true,
  settingsWide = false,
  flexLayout = false,
} = defineProps<{
  showCommonConfig?: boolean;
  independentScroll?: boolean;
  settingsWide?: boolean;
  flexLayout?: boolean;
}>();

const slots = useSlots();
const hasSideNavigation = computed(() => slots["side-navigation"] != null);
const hasEditorLayout = computed(
  () =>
    slots["common-config"] != null || slots["side-navigation"] != null || slots.settings != null,
);
</script>

<style lang="scss" scoped>
.input-frame-layout {
  display: grid;
  grid-template-columns: minmax(12rem, 14rem) minmax(0, 1fr) minmax(17rem, 22rem);
  gap: 0.75rem;
  min-width: 0;
  min-height: 100%;
}

.input-frame-layout--without-side-navigation {
  grid-template-columns: minmax(0, 1fr) minmax(17rem, 22rem);
}

.input-frame-layout--without-side-navigation.input-frame-layout--wide-settings {
  grid-template-columns: minmax(0, 1fr) minmax(28rem, 44rem);
}

.input-frame-layout__common-config {
  grid-column: 1 / -1;
  min-width: 0;
}

.input-frame-layout__side-navigation,
.input-frame-layout__settings {
  position: sticky;
  top: 0;
  align-self: start;
  min-width: 0;
  height: 100%;
}

.input-frame-layout__content {
  min-width: 0;
}

.input-frame-layout--independent-scroll {
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  min-height: 0;
  overflow: hidden;

  > :is(
    .input-frame-layout__side-navigation,
    .input-frame-layout__content,
    .input-frame-layout__settings
  ) {
    grid-row: 2;
    align-self: stretch;
    min-height: 0;
    height: auto;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
}

@media (max-width: 1000px) {
  .input-frame-layout {
    grid-template-columns: 1fr;
  }

  .input-frame-layout__common-config {
    grid-column: auto;
  }

  .input-frame-layout__side-navigation,
  .input-frame-layout__settings {
    position: static;
  }

  .input-frame-layout--independent-scroll {
    display: grid;
    height: auto;
    overflow: visible;

    > :is(
      .input-frame-layout__side-navigation,
      .input-frame-layout__content,
      .input-frame-layout__settings
    ) {
      grid-row: auto;
      height: auto;
      overflow-y: visible;
    }
  }
}
</style>
